import i18next, { TFunction } from "i18next";
import * as yup from "yup";

import { removeMetadataPrefix } from "../helpers";

const generateControlPathNoEntityContext = (
  path: string,
  t: TFunction,
): unknown => {
  if (!path) {
    return path;
  }

  const translationKey = `formLabel.${removeMetadataPrefix(path)}`;

  if (i18next.exists(translationKey)) {
    return t(translationKey);
  } else if (i18next.exists(`ride.${translationKey}`)) {
    /* for some cases validations of ride entity in constructed before entity context is loaded */
    return t(`ride.${translationKey}`);
  }

  return path;
};

export const yupLocaleNoEntityContext = (t: TFunction): yup.LocaleObject => ({
  mixed: {
    default: ({ path }) =>
      t("validation.invalid", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    required: ({ path }) =>
      t("validation.required", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    defined: ({ path }) =>
      t("validation.mustBeDefined", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    notNull: ({ path }) =>
      t("validation.mustNotBeNull", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    oneOf: ({ path, values }) =>
      t("validation.oneOf", {
        path: generateControlPathNoEntityContext(path, t),
        values,
      }),
    notOneOf: ({ path, values }) =>
      t("validation.notOneOf", {
        path: generateControlPathNoEntityContext(path, t),
        values,
      }),
    notType: ({ path, type, value, originalValue }) => {
      const castMsg =
        originalValue !== null && originalValue !== value
          ? ` (cast from the value \`${originalValue}\`).`
          : ".";

      return type !== "mixed"
        ? `${path} must be a \`${type}\` type, ` +
            `but the final value was: \`${originalValue}\`` +
            castMsg
        : `${path} must match the configured type. ` +
            `The validated value was: \`${originalValue}\`` +
            castMsg;
    },
  },
  string: {
    // TODO: Add fixes to required
    length: ({ path, length }) =>
      t("validation.exactLengthString", {
        path: generateControlPathNoEntityContext(path, t),
        length,
      }),
    min: ({ path, min }) =>
      t("validation.minLength", {
        path: generateControlPathNoEntityContext(path, t),
        min,
      }),
    max: ({ path, max }) =>
      t("validation.maxLength", {
        path: generateControlPathNoEntityContext(path, t),
        max,
      }),
    matches: ({ path, regex }) =>
      t("validation.matches", {
        path: generateControlPathNoEntityContext(path, t),
        regex,
      }),
    email: ({ path }) =>
      t("validation.email", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    url: ({ path }) =>
      t("validation.url", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    uuid: ({ path }) =>
      t("validation.uuid", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    datetime: ({ path }) =>
      t("validation.datetime", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    datetime_precision: ({ path, precision }) =>
      t("validation.datetimePrecision", {
        path: generateControlPathNoEntityContext(path, t),
        precision,
      }),
    datetime_offset: ({ path }) =>
      t("validation.datetimeOffset", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    trim: ({ path }) =>
      t("validation.trim", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    lowercase: ({ path }) =>
      t("validation.lowercase", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    uppercase: ({ path }) =>
      t("validation.uppercase", {
        path: generateControlPathNoEntityContext(path, t),
      }),
  },
  number: {
    min: ({ path, min }) =>
      t("validation.minValue", {
        path: generateControlPathNoEntityContext(path, t),
        min,
      }),
    max: ({ path, max }) =>
      t("validation.maxValue", {
        path: generateControlPathNoEntityContext(path, t),
        max,
      }),
    lessThan: ({ path, less }) =>
      t("validation.lessThan", {
        path: generateControlPathNoEntityContext(path, t),
        less,
      }),
    moreThan: ({ path, more }) =>
      t("validation.moreThan", {
        path: generateControlPathNoEntityContext(path, t),
        more,
      }),
    positive: ({ path }) =>
      t("validation.positive", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    negative: ({ path }) =>
      t("validation.negative", {
        path: generateControlPathNoEntityContext(path, t),
      }),
    integer: ({ path }) =>
      t("validation.integer", {
        path: generateControlPathNoEntityContext(path, t),
      }),
  },
  date: {
    min: ({ path, min }) =>
      t("validation.beforeDate", {
        path: generateControlPathNoEntityContext(path, t),
        min,
      }),
    max: ({ path, max }) =>
      t("validation.afterDate", {
        path: generateControlPathNoEntityContext(path, t),
        max,
      }),
  },
  boolean: {
    isValue: ({ path, value }) =>
      t("validation.isValue", {
        path: generateControlPathNoEntityContext(path, t),
        value,
      }),
  },
  object: {
    noUnknown: ({ path, unknown }) =>
      t("validation.noUnknown", {
        path: generateControlPathNoEntityContext(path, t),
        unknown,
      }),
  },
  array: {
    min: ({ path, min }) =>
      t("validation.minLengthArray", {
        path: generateControlPathNoEntityContext(path, t),
        min,
      }),
    max: ({ path, max }) =>
      t("validation.maxLengthArray", {
        path: generateControlPathNoEntityContext(path, t),
        max,
      }),
    length: ({ path, length }) =>
      t("validation.exactLengthArray", {
        path: generateControlPathNoEntityContext(path, t),
        length,
      }),
  },
  tuple: {
    notType: params => {
      const { path, value, spec } = params;
      const typeLen = spec.types.length;

      if (Array.isArray(value)) {
        if (value.length < typeLen) {
          return `${path} tuple value has too few items, expected a length of ${typeLen} but got ${value.length} for value: \`${value}\``;
        }

        if (value.length > typeLen) {
          return `${path} tuple value has too many items, expected a length of ${typeLen} but got ${value.length} for value: \`${value}\``;
        }
      }

      return "unknown tuple validation error";
      // return ValidationError.formatError(mixed.notType, params);
    },
  },
});

/*

original yup messages (for reference in translating)

export let mixed: Required<MixedLocale> = {
  default: '${path} is invalid',
  required: '${path} is a required field',
  defined: '${path} must be defined',
  notNull: '${path} cannot be null',
  oneOf: '${path} must be one of the following values: ${values}',
  notOneOf: '${path} must not be one of the following values: ${values}',
  notType: ({ path, type, value, originalValue }) => {
    const castMsg =
      originalValue != null && originalValue !== value
        ? ` (cast from the value \`${printValue(originalValue, true)}\`).`
        : '.';

    return type !== 'mixed'
      ? `${path} must be a \`${type}\` type, ` +
          `but the final value was: \`${printValue(value, true)}\`` +
          castMsg
      : `${path} must match the configured type. ` +
          `The validated value was: \`${printValue(value, true)}\`` +
          castMsg;
  },
};

export let string: Required<StringLocale> = {
  length: '${path} must be exactly ${length} characters',
  min: '${path} must be at least ${min} characters',
  max: '${path} must be at most ${max} characters',
  matches: '${path} must match the following: "${regex}"',
  email: '${path} must be a valid email',
  url: '${path} must be a valid URL',
  uuid: '${path} must be a valid UUID',
  datetime: '${path} must be a valid ISO date-time',
  datetime_precision:
    '${path} must be a valid ISO date-time with a sub-second precision of exactly ${precision} digits',
  datetime_offset:
    '${path} must be a valid ISO date-time with UTC "Z" timezone',
  trim: '${path} must be a trimmed string',
  lowercase: '${path} must be a lowercase string',
  uppercase: '${path} must be a upper case string',
};

export let number: Required<NumberLocale> = {
  min: '${path} must be greater than or equal to ${min}',
  max: '${path} must be less than or equal to ${max}',
  lessThan: '${path} must be less than ${less}',
  moreThan: '${path} must be greater than ${more}',
  positive: '${path} must be a positive number',
  negative: '${path} must be a negative number',
  integer: '${path} must be an integer',
};

export let date: Required<DateLocale> = {
  min: '${path} field must be later than ${min}',
  max: '${path} field must be at earlier than ${max}',
};

export let boolean: BooleanLocale = {
  isValue: '${path} field must be ${value}',
};

export let object: Required<ObjectLocale> = {
  noUnknown: '${path} field has unspecified keys: ${unknown}',
};

export let array: Required<ArrayLocale> = {
  min: '${path} field must have at least ${min} items',
  max: '${path} field must have less than or equal to ${max} items',
  length: '${path} must have ${length} items',
};

export let tuple: Required<TupleLocale> = {
  notType: (params) => {
    const { path, value, spec } = params;
    const typeLen = spec.types.length;
    if (Array.isArray(value)) {
      if (value.length < typeLen)
        return `${path} tuple value has too few items, expected a length of ${typeLen} but got ${
          value.length
        } for value: \`${printValue(value, true)}\``;
      if (value.length > typeLen)
        return `${path} tuple value has too many items, expected a length of ${typeLen} but got ${
          value.length
        } for value: \`${printValue(value, true)}\``;
    }

    return ValidationError.formatError(mixed.notType, params);
  },
};

export default Object.assign(Object.create(null), {
  mixed,
  string,
  number,
  date,
  object,
  array,
  boolean,
  tuple,
}) as LocaleObject;

 */
